import { InvoiceInterface, InvoiceStatus } from "@contracts/interfaces/invoice.interface";
import { CustomerInterface } from "@contracts/interfaces/customer.interface";
import { LineItem } from "@contracts/interfaces/lineitem.interface";

export class InvoiceFactory {
  public static newInvoice(type: string = "standard", customer: CustomerInterface | null = null): InvoiceInterface {
    const now = new Date();
    return {
      type,
      status: InvoiceStatus.Unpaid,
      customer,
      number: null,
      custom: "",
      dateCreated: now,
      dateDue: now,
      subTotal: 0,
      total: 0,
      paymentsMade: [],
      pdf: {
        file: "invoice-" + type
      },
      lineItems: [
        {
          sku: null,
          item: "",
          desc: "",
          qty: 1,
          price: 0,
        }
      ],
      gst: 0,
      isGSTIncluded: true,
      communicationLog: []
    };
  }

  public static validate(invoice: InvoiceInterface | any): InvoiceInterface {
    if (!invoice) {
      return this.newInvoice("standard", null);
    }

    const clone: InvoiceInterface = { ...(invoice as any) };

    clone.type = clone.type || "standard";
    clone.status = (clone.status as InvoiceStatus) || InvoiceStatus.Unpaid;
    clone.customer = clone.customer || null;
    clone.number = clone.number || null;
    clone.custom = clone.custom || "";
    clone.dateCreated = clone.dateCreated ? new Date(clone.dateCreated) : new Date();
    clone.dateDue = clone.dateDue ? new Date(clone.dateDue) : new Date();
    clone.subTotal = 0;
    clone.total = 0;
    clone.gst = 0;
    clone.paymentsMade = Array.isArray(clone.paymentsMade) ? clone.paymentsMade : [];
    clone.pdf = clone.pdf || { file: "invoice-" + clone.type };
    clone.isGSTIncluded = clone.isGSTIncluded !== false;
    clone.communicationLog = clone.communicationLog || [];

    clone.lineItems = Array.isArray(clone.lineItems) ? clone.lineItems : [];
    if (clone.lineItems.length === 0) {
      clone.lineItems = [{
        sku: null,
        item: "",
        desc: "",
        qty: 1,
        price: 0
      }];
    }

    for (let i = 0; i < clone.lineItems.length; i++) {
      const line = { ...clone.lineItems[i] } as LineItem;
      const price = +line.price || 0;
      const qty = +line.qty || 0;
      const lineTotal = price * qty;
      line.item = line.item || "Service";
      line.desc = line.desc || "";
      line.qty = qty;
      line.price = price;
      clone.lineItems[i] = line;

      if (clone.isGSTIncluded) {
        clone.subTotal += (lineTotal - (lineTotal / 11));
        clone.gst += lineTotal / 11;
        clone.total += lineTotal;
      } else {
        clone.subTotal += lineTotal;
        clone.gst += lineTotal * 0.1;
        clone.total += (lineTotal + (lineTotal * 0.1));
      }
    }

    let amountDue = clone.total;
    let didDoRefund = false;
    for (let i = 0; i < clone.paymentsMade.length; i++) {
      const paymentMade = { ...clone.paymentsMade[i] };
      paymentMade.date = paymentMade.date ? new Date(paymentMade.date) : new Date();
      paymentMade.paymentId = paymentMade.paymentId || `${clone.number ?? 'draft'}-${paymentMade.date?.valueOf() ?? Date.now()}-${i}`;
      paymentMade.transactionSha1 = paymentMade.transactionSha1 ?? null;
      clone.paymentsMade[i] = paymentMade;

      const amt = +paymentMade?.amount || 0;
      if (amt < 0) {
        didDoRefund = true;
      }
      amountDue -= amt;
    }
    clone.amountDue = amountDue;

    if (didDoRefund) {
      clone.status = InvoiceStatus.Refund;
    } else if (amountDue > 0) {
      clone.status = InvoiceStatus.Unpaid;
    } else {
      clone.status = InvoiceStatus.Paid;
    }

    return clone;
  }
}
